/**
 * 全局弹窗（`modals`）：对外 action 入口。
 * 统一通过 `FJModalsAction` 访问。
 */
import type { AppThunk } from '../appThunk';
import {
    hideProgressModal,
    hideDislikeModal,
    hideRenameModal,
    hideWarningModal,
    setProgressModalProgress,
    showProgressModal,
    showDislikeModal,
    showRenameModal,
    showWarningModal,
    type FJProgressModalData,
    type FJRenameModalData,
    type FJWarningModalData,
} from '../slice/FJModalsSlice';
import FJGoogleDriveModalSlice from '@src/redux/slice/modal/FJGoogleDriveModalSlice.ts';
import FJLoadingModalSlice from '@src/redux/slice/modal/FJLoadingModalSlice.ts';
import FJEventBridgeManager, { FJEventBridgeTarget } from '@src/manager/FJEventBridgeManager.ts';
import FJCutVideoSlice from '@src/redux/slice/modal/FJCutVideoSlice.ts';
import FJShareSlice from '@src/redux/slice/FJShareSlice.ts';
import FJPublishManager from '@src/manager/publish/FJPublishManager.ts';
import FJTrimAction from '@src/redux/action/FJTrimAction.ts';
import FJRequirementsUtil from '@src/util/FJRequirementsUtil';

export type {
    FJProgressModalData,
    FJRenameModalData,
    FJRenameModalScope,
    FJDislikeModalData,
    FJWarningModalData,
    FJWarningModalFooterBtn,
} from '../slice/FJModalsSlice';

export const FJModalsAction = {
    showWarningModalAction:
        (payload: Omit<FJWarningModalData, 'display'>): AppThunk =>
        dispatch => {
            dispatch(showWarningModal(payload));
        },

    hideWarningModalAction: (): AppThunk => dispatch => {
        dispatch(hideWarningModal());
    },

    /** 异步确认成功后关弹窗并执行打开时注册的 `closeCallback`（与 `hideWarningModalAction` 区分：取消不关回调） */
    completeWarningModalAction: (): AppThunk => (dispatch, getState) => {
        const { closeCallback } = getState().modals.warningModalData;
        dispatch(hideWarningModal());
        closeCallback?.();
    },

    showProgressModalAction:
        (payload: Omit<FJProgressModalData, 'display'>, closeCallback?: (data: any) => void): AppThunk =>
        dispatch => {
            const bridge = FJEventBridgeManager.getBridge(FJEventBridgeTarget.PROGRESS_MODAL);
            bridge.bindExecuteFunction(closeCallback);
            dispatch(showProgressModal(payload));
        },

    updateProgressModalAction:
        (payload: { progress: number }): AppThunk =>
        dispatch => {
            dispatch(setProgressModalProgress(payload));
        },

    hideProgressModalAction: (): AppThunk => dispatch => {
        dispatch(hideProgressModal());
    },

    showRenameModalAction:
        (payload: Omit<FJRenameModalData, 'display'>): AppThunk =>
        dispatch => {
            dispatch(showRenameModal(payload));
        },

    hideRenameModalAction: (): AppThunk => dispatch => {
        dispatch(hideRenameModal());
    },

    showDislikeModalAction:
        (payload: { targetDetailId: string }): AppThunk =>
        dispatch => {
            dispatch(showDislikeModal(payload));
        },

    hideDislikeModalAction: (): AppThunk => dispatch => {
        dispatch(hideDislikeModal());
    },
    showGoogleDriveImportModalAction: (): AppThunk => dispatch => {
        dispatch(FJGoogleDriveModalSlice.actions.setIsShow(true));
    },
    hideGoogleDriveImportModalAction: (): AppThunk => dispatch => {
        dispatch(FJGoogleDriveModalSlice.actions.setIsShow(false));
    },
    showLoadingModalAction:
        (message: string, closeCallback?: (data: any) => void, onlyLoading = false): AppThunk =>
        dispatch => {
            const bridge = FJEventBridgeManager.getBridge(FJEventBridgeTarget.LOADING_MODAL);
            bridge.bindExecuteFunction(closeCallback);
            dispatch(FJLoadingModalSlice.actions.setMessage(message));
            dispatch(FJLoadingModalSlice.actions.setIsShow(true));
            dispatch(FJLoadingModalSlice.actions.setIsCancel(false));
            dispatch(FJLoadingModalSlice.actions.setOnlyLoading(onlyLoading));
        },
    hideLoadingModalAction: (): AppThunk => dispatch => {
        dispatch(FJLoadingModalSlice.actions.setMessage(''));
        dispatch(FJLoadingModalSlice.actions.setIsShow(false));
    },
    showTrimModalAction: (): AppThunk => dispatch => {
        dispatch(FJCutVideoSlice.actions.setIsShow(true));
    },
    hideTrimModalAction: (): AppThunk => dispatch => {
        dispatch(FJTrimAction.pauseAction());
        dispatch(FJCutVideoSlice.actions.setIsShow(false));
    },
    showShareModalAction: (): AppThunk => dispatch => {
        dispatch(FJShareSlice.actions.setIsShow(true));
    },
    hideShareModalAction:
        (from?: string): AppThunk =>
        dispatch => {
            if (from) {
                FJRequirementsUtil.eaLog('publish_window_close_and_cancel_click', {
                    source: from,
                    project_id: FJPublishManager.eventId,
                    short_id: FJPublishManager.subId,
                    has_edited_content: FJPublishManager.isEditContent(),
                });
            }
            FJPublishManager.saveTemporaryData();
            dispatch(FJShareSlice.actions.setIsShow(false));
            dispatch(hideProgressModal());
            dispatch(FJLoadingModalSlice.actions.setIsShow(false));
        },
} as const;
